import { useDispatch, useSelector } from "react-redux";
import { Fragment, useEffect, useState } from "react";
import PostRequest from "../../services/PostRequest";
import { setZipCodeList } from "../../reducer/zipCode/ZipCode.action";
import { showToaster, handleCopy } from "../../common/common";
import moment from "moment";

// Map Domain.country_code -> display name for the Country column. The API only
// exposes country_code (there is no `country` field), so the column was always
// blank before.
const COUNTRY_NAMES = {
  AU: "Australia", US: "United States", CA: "Canada", MX: "Mexico",
  BR: "Brazil", GB: "United Kingdom", DE: "Germany", FR: "France",
  IT: "Italy", ES: "Spain", PL: "Poland", IE: "Ireland", TR: "Türkiye",
  IN: "India", JP: "Japan", SG: "Singapore", AE: "UAE", SA: "Saudi Arabia",
  EG: "Egypt", NL: "Netherlands", SE: "Sweden", ZA: "South Africa",
};

export const PostCodesTable = () => {
  const dispatch = useDispatch();
  const zipCodesList = useSelector((item) => item.zipCodeReducer.zipCodesList);
  const [loader, setLoader] = useState(false);

  // Zipcode.STATUS_CHOICES: active / pending / in-progress / inactive / failed
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "active":
        return "mca-badge--soft-success";
      case "in-progress":
      case "pending":
        return "mca-badge--soft-info";
      case "failed":
        return "mca-badge--soft-danger";
      case "inactive":
      default:
        return "mca-badge--soft-secondary";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "active":
        return "Active";
      case "pending":
        return "Pending";
      case "in-progress":
        return "In progress";
      case "inactive":
        return "Inactive";
      case "failed":
        return "Failed";
      default:
        return status;
    }
  };

  const handleDeleteZipCode = async (id) => {
    try {
      if (!window.confirm("Are you sure you want to delete this postcode / ZIP?"))
        return false;

      const data = {
        zipcode_id: id,
      };
      await PostRequest("/zipcodes/delete-zipcode/", data);
      showToaster("Postcode / ZIP deleted.");
      dispatch(setZipCodeList());
    } catch (error) {
      showToaster(error.message, "error");
    }
  };

  const handleCopyPostcode = async (postcode) => {
    await handleCopy(postcode);
    showToaster("Postcode / ZIP copied.");
  };

  const loadZipCodes = async () => {
    try {
      setLoader(true);
      await dispatch(setZipCodeList());
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  // The parent (PostCodes) now fetches the list on mount. Only fetch here if
  // the store is somehow empty, so we don't double-fetch and flash the loader
  // over data the parent already loaded.
  useEffect(() => {
    if (!zipCodesList || zipCodesList.length === 0) {
      loadZipCodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loader) {
    return (
      <table className="mca-table">
        <thead>
          <tr>
            <th>
              <span className="mca-th-sort">
                Postcode
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m8 9 4-4 4 4" />
                  <path d="m16 15-4 4-4-4" />
                </svg>
              </span>
            </th>
            <th>Domain</th>
            <th className="hide-md">Country</th>
            <th>Status</th>
            <th className="hide-md">Created</th>
            <th style={{ textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>
              Loading...
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table className="mca-table">
      <thead>
        <tr>
          <th>
            <span className="mca-th-sort">
              Postcode/ZIP
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m8 9 4-4 4 4" />
                <path d="m16 15-4 4-4-4" />
              </svg>
            </span>
          </th>
          <th>Domain</th>
          <th className="hide-md">Country</th>
          <th>Status</th>
          <th className="hide-md">Created</th>
          <th style={{ textAlign: "right" }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {zipCodesList && zipCodesList.length > 0 ? (
          zipCodesList.map((row) => (
            <tr key={row.id}>
              <td className="mca-cell-mono cell-strong">{row.zipcode}</td>
              <td>
                <span className="flagcell">
                  <span className="fl">
                    {row.domain?.country_code || ""}
                  </span>
                  {row.domain?.domain || ""}
                </span>
              </td>
              <td className="hide-md cell-muted">
                {COUNTRY_NAMES[row.domain?.country_code] ||
                  row.domain?.country_code ||
                  ""}
              </td>
              <td>
                <span className={`mca-badge ${getStatusBadgeClass(row.status)}`}>
                  <span className="dot"></span>
                  {getStatusLabel(row.status)}
                </span>
              </td>
              <td className="hide-md cell-muted" style={{ fontSize: "13px" }}>
                {row.created_at
                  ? moment(row.created_at).fromNow()
                  : ""}
              </td>
              <td>
                <div className="mca-row-actions">
                  <button
                    className="mca-btn mca-btn--ghost mca-btn--icon mca-btn--sm"
                    title="Copy"
                    aria-label="Copy postcode"
                    onClick={() => handleCopyPostcode(row.zipcode)}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="9" y="9" width="11" height="11" rx="2" />
                      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                    </svg>
                  </button>
                  <button
                    className="mca-btn mca-btn--ghost mca-btn--icon mca-btn--sm"
                    title="Delete"
                    aria-label="Delete postcode"
                    onClick={() => handleDeleteZipCode(row.id)}
                    style={{ color: "var(--mca-danger)" }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 7h16" />
                      <path d="M9 7V4h6v3" />
                      <path d="M6 7l1 13h10l1-13" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>
              No postcodes
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};
